/**
 * Orders-screen line visibility helpers.
 *
 * Manager-removed lines (quantity 0, never receipted) stay hidden for audit.
 * Receipt-zeroed lines (ordered > 0 but 0 received on a saved receipt) must
 * STAY visible on the original order with received = 0 — removing them makes
 * the order look like it was never placed for that product.
 */
import { getQuantity, getOrderedQuantity } from './priceFunctions'

const parseFiniteQuantity = value => {
  if (value === null || value === undefined || value === '') return NaN
  const num = Number(value)
  return Number.isFinite(num) ? num : NaN
}

export function getPopulatedReceipting(item = {}) {
  const receipting = item?.receipting
  if (
    !receipting ||
    typeof receipting !== 'object' ||
    Array.isArray(receipting)
  ) {
    return null
  }
  const keys = Object.keys(receipting)
  if (keys.length === 1 && keys[0] === '_id') return null
  return receipting
}

/** True when the line has a saved receipt record (any status except unsaved). */
export function hasSavedReceipting(item = {}) {
  const receipting = getPopulatedReceipting(item)
  if (!receipting) return false
  const status = receipting.status
  if (!status || status === 'Not Saved') return false
  // Lines carrying an edit outcome are saved even when status is missing.
  return true
}

/** Ordered > 0 but 0 actually received on a saved receipt. */
export function isReceiptZeroedOrderLine(item = {}) {
  if (!hasSavedReceipting(item)) return false
  return getOrderedQuantity(item) > 0 && getQuantity(item) === 0
}

/**
 * Orders screen visibility: keep positive-quantity lines and receipt-zeroed
 * lines; hide only manager-removed (never receipted, quantity 0) lines.
 */
export function isVisibleOrdersLine(item = {}) {
  if (Number(item?.quantity) > 0) return true
  return isReceiptZeroedOrderLine(item)
}

/** Ordered vs received presentation for a single order line. */
export function getOrderedVsReceived(item = {}) {
  const ordered = getOrderedQuantity(item)
  const received = getQuantity(item)
  return {
    ordered,
    received,
    hasDiscrepancy: hasSavedReceipting(item) && received !== ordered,
  }
}

export { parseFiniteQuantity }
